import { isDojoCourseSlug } from "@/lib/luagate/dojo-list";
import { isUnlistedCourse } from "@/lib/unlisted-courses";
import type {
  PathLearningAsset,
  PathLearningAssetAuditStatus,
  PathLearningAssetRelation,
} from "./types";

const RELATION_RANK: Record<PathLearningAssetRelation, number> = {
  prerequisite: 0,
  primary: 1,
  practice: 2,
  assessment: 2,
  supplement: 3,
  demonstration: 3,
  review: 3,
  reference: 3,
};

const RELATION_LABEL: Record<PathLearningAssetRelation, string> = {
  primary: "主要教材",
  prerequisite: "事前学習",
  supplement: "補助",
  demonstration: "実演教材",
  practice: "追加演習",
  assessment: "理解度確認",
  review: "復習",
  reference: "参考",
};

const AUDIT_STATUS_LABEL: Record<PathLearningAssetAuditStatus, string> = {
  linked: "接続済み",
  no_course_needed: "コース不要",
  rewrite_to_launcher: "本文調整待ち",
  overlap_review: "要レビュー",
  split_item: "分割レビュー",
  needs_new_item: "新規項目候補",
  blocked: "確認待ち",
  unreviewed: "未監査",
};

export type PathLearningAssetFilter =
  | "all"
  | "course"
  | "lesson"
  | "no_course_needed"
  | "needs_review";

export function learningAssetKindLabel(asset: PathLearningAsset): string {
  return asset.kind === "learn_lesson" ? "関連レッスン" : "関連コース";
}

export function learningAssetRelationLabel(
  relation: PathLearningAssetRelation,
): string {
  return RELATION_LABEL[relation];
}

export function learningAssetAuditStatusLabel(
  status: PathLearningAssetAuditStatus,
): string {
  return AUDIT_STATUS_LABEL[status];
}

/**
 * 関連教材の行き先。レッスンへの行き先には、項目へ戻る印 (returnQuery) を付ける。コースの頁には付けない (戻る入口を出すのはレッスンの頁)
 */
export function learningAssetHref(asset: PathLearningAsset, returnQuery = ""): string {
  const courseHref = `/learn/courses/${encodeURIComponent(asset.course.slug)}`;
  if (asset.kind !== "learn_lesson" || !asset.lesson?.slug) return courseHref;
  const lessonHref = `${courseHref}/${encodeURIComponent(asset.lesson.slug)}`;
  return returnQuery ? `${lessonHref}?${returnQuery}` : lessonHref;
}

export function sortPathLearningAssets(
  assets: readonly PathLearningAsset[],
): PathLearningAsset[] {
  return [...assets].sort(
    (a, b) =>
      RELATION_RANK[a.relationType] - RELATION_RANK[b.relationType] ||
      a.displayOrder - b.displayOrder ||
      a.course.title.localeCompare(b.course.title, "ja"),
  );
}

export function matchesLearningAssetQuery(
  assets: readonly PathLearningAsset[],
  query: string,
): boolean {
  const normalized = query.trim().toLocaleLowerCase("ja-JP");
  if (!normalized) return true;
  return assets.some((asset) =>
    [
      asset.course.slug,
      asset.course.title,
      asset.lesson?.slug ?? "",
      asset.lesson?.title ?? "",
    ].some((value) => value.toLocaleLowerCase("ja-JP").includes(normalized)),
  );
}

export function matchesLearningAssetFilter(
  assets: readonly PathLearningAsset[],
  auditStatus: PathLearningAssetAuditStatus | null | undefined,
  filter: PathLearningAssetFilter,
): boolean {
  switch (filter) {
    case "course":
      return assets.some((asset) => asset.kind === "learn_course");
    case "lesson":
      return assets.some((asset) => asset.kind === "learn_lesson");
    case "no_course_needed":
      return auditStatus === "no_course_needed";
    case "needs_review":
      return (
        auditStatus === "rewrite_to_launcher" ||
        auditStatus === "overlap_review" ||
        auditStatus === "split_item" ||
        auditStatus === "needs_new_item" ||
        auditStatus === "blocked" ||
        auditStatus === "unreviewed"
      );
    default:
      return true;
  }
}

export function formatCourseDuration(
  hours: number | null | undefined,
): string | null {
  if (hours == null || !Number.isFinite(hours) || hours <= 0) return null;
  if (hours < 1) return `約 ${Math.round(hours * 60)} 分`;
  return `約 ${Number.isInteger(hours) ? hours : hours.toFixed(1)} 時間`;
}

/** 一覧から隠すコースは、受講済みか道場のときだけ見せる。札と詳細の教材欄で同じ判定を使う */
export function isVisiblePathLearningAsset(asset: PathLearningAsset): boolean {
  return (
    !isUnlistedCourse(asset.course.slug) ||
    isDojoCourseSlug(asset.course.slug) ||
    (asset.course.completedLessonCount ?? 0) > 0
  );
}

/** 関連教材のうち、練習道場のコースの slug。なければ null */
export function dojoCourseSlugOf(assets: readonly PathLearningAsset[] | undefined): string | null {
  return assets?.find((asset) => isDojoCourseSlug(asset.course.slug))?.course.slug ?? null;
}
